import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const seededShuffle = (array, seed) => {
  const out = [...array];
  let s = seed || 1;
  const rand = () => {
    const x = Math.sin(s++) * 10000;
    return x - Math.floor(x);
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// A pair contributes two cards. If it has both text and image → one word card
// and one image card. If only one side is filled → both cards share that side
// (text-text or image-image) so the pair is still playable.
const buildCardsFromPairs = (pairs) => {
  const cards = [];
  pairs.forEach((p) => {
    const hasText = (p.text || '').trim().length > 0;
    const hasImage = !!p.imageUrl;
    if (!hasText && !hasImage) return;
    if (hasText && hasImage) {
      cards.push({ key: `${p.id}-w`, pairId: p.id, kind: 'text', value: p.text });
      cards.push({ key: `${p.id}-i`, pairId: p.id, kind: 'image', value: p.imageUrl });
    } else if (hasImage) {
      cards.push({ key: `${p.id}-i1`, pairId: p.id, kind: 'image', value: p.imageUrl });
      cards.push({ key: `${p.id}-i2`, pairId: p.id, kind: 'image', value: p.imageUrl });
    } else {
      cards.push({ key: `${p.id}-w1`, pairId: p.id, kind: 'text', value: p.text });
      cards.push({ key: `${p.id}-w2`, pairId: p.id, kind: 'text', value: p.text });
    }
  });
  return cards;
};

const MemoryElement = ({
  element,
  isEditor = false,
  disabled = false,
  slideScale = 1,
}) => {
  const { t } = useTranslation();
  const { content, id } = element;
  const {
    pairs = [],
    columns: configuredCols = 0,
    cardBackColor = '#2A4D25',
    cardFrontColor = '#ffffff',
    matchedBorderColor = '#86efac',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 18,
  } = content || {};

  const playablePairs = useMemo(
    () => pairs.filter((p) => (p.text && p.text.trim()) || p.imageUrl),
    [pairs],
  );

  // Stable seed per element so the layout is identical for every student and
  // across renders. A new "reset" bumps the local extra seed to re-shuffle.
  const baseSeed = useMemo(() => {
    if (!id) return 1;
    return id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  }, [id]);
  const [resetToken, setResetToken] = useState(0);

  const cards = useMemo(() => {
    const base = buildCardsFromPairs(playablePairs);
    if (base.length === 0) return [];
    return seededShuffle(base, baseSeed + resetToken * 7919);
  }, [playablePairs, baseSeed, resetToken]);

  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [wrongKeys, setWrongKeys] = useState([]);
  const [justMatched, setJustMatched] = useState([]);
  const timeoutsRef = useRef([]);

  const clearTimeouts = () => {
    timeoutsRef.current.forEach((h) => clearTimeout(h));
    timeoutsRef.current = [];
  };
  useEffect(() => () => clearTimeouts(), []);

  const resetBoard = () => {
    clearTimeouts();
    setFlipped([]);
    setMatched([]);
    setWrongKeys([]);
    setJustMatched([]);
    setResetToken((n) => n + 1);
  };

  // Reset the gameplay state when the configured pairs change (editor edits).
  useEffect(() => {
    if (isEditor) {
      setFlipped([]);
      setMatched([]);
      setWrongKeys([]);
      setJustMatched([]);
    }
  }, [playablePairs.length, isEditor]);

  const interactive = !isEditor && !disabled;

  const handleFlip = (card) => {
    if (!interactive) return;
    if (flipped.length === 2) return;
    if (matched.includes(card.pairId)) return;
    if (flipped.find((c) => c.key === card.key)) return;

    const next = [...flipped, card];
    setFlipped(next);

    if (next.length === 2) {
      const [a, b] = next;
      if (a.pairId === b.pairId && a.key !== b.key) {
        const h = setTimeout(() => {
          setMatched((m) => [...m, a.pairId]);
          setJustMatched([a.key, b.key]);
          setFlipped([]);
          const h2 = setTimeout(() => setJustMatched([]), 600);
          timeoutsRef.current.push(h2);
        }, 350);
        timeoutsRef.current.push(h);
      } else {
        const h = setTimeout(() => {
          setWrongKeys([a.key, b.key]);
          const h2 = setTimeout(() => {
            setWrongKeys([]);
            setFlipped([]);
          }, 550);
          timeoutsRef.current.push(h2);
        }, 500);
        timeoutsRef.current.push(h);
      }
    }
  };

  const s = typeof slideScale === 'number' && slideScale > 0 ? slideScale : 1;
  const scaledFontSize = Math.max(8, fontSize * s);
  const scaledGap = Math.max(4, Math.round(12 * s));
  const scaledPad = Math.max(4, Math.round(12 * s));

  // Choose a column count that keeps the grid roughly rectangular when the
  // teacher hasn't set one explicitly. Default picks a layout close to a
  // golden-rectangle-ish shape (prefers wider than tall when possible).
  const cols = useMemo(() => {
    if (cards.length === 0) return 1;
    if (configuredCols && configuredCols > 0) return Math.min(configuredCols, cards.length);
    const n = cards.length;
    if (n <= 4) return n;
    if (n <= 6) return 3;
    if (n <= 8) return 4;
    if (n <= 12) return 4;
    if (n <= 16) return 4;
    return Math.ceil(Math.sqrt(n * 1.4));
  }, [cards.length, configuredCols]);

  const allMatched = playablePairs.length > 0 && matched.length === playablePairs.length;

  if (playablePairs.length === 0) {
    return (
      <div
        className="w-full h-full rounded-xl flex items-center justify-center text-center text-sm text-gray-400 p-4"
        style={{ backgroundColor: '#ffffff', border: '1px dashed #e5e7eb' }}
      >
        {t('editor.configPanels.memory.addPairsHint', {
          defaultValue: 'Додайте пари у налаштуваннях праворуч',
        })}
      </div>
    );
  }

  return (
    <div className="w-full h-full relative flex flex-col">
      <style>{`
        .mem-card { perspective: 900px; }
        .mem-inner { position: relative; width: 100%; height: 100%; transition: transform 0.5s cubic-bezier(.2,.8,.2,1); transform-style: preserve-3d; }
        .mem-face { position: absolute; inset: 0; -webkit-backface-visibility: hidden; backface-visibility: hidden; border-radius: 0.75rem; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .mem-back { transform: rotateY(180deg); }
        .mem-is-flipped .mem-inner { transform: rotateY(180deg); }
        @keyframes mem-bounce { 0%{transform:rotateY(180deg) scale(1)} 40%{transform:rotateY(180deg) scale(1.08)} 100%{transform:rotateY(180deg) scale(1)} }
        @keyframes mem-shake { 0%,100%{transform:rotateY(180deg) translateX(0)} 25%{transform:rotateY(180deg) translateX(-5px)} 50%{transform:rotateY(180deg) translateX(5px)} 75%{transform:rotateY(180deg) translateX(-3px)} }
        .mem-matched .mem-inner { animation: mem-bounce 0.5s ease-out; }
        .mem-wrong .mem-inner { animation: mem-shake 0.4s ease-in-out; }
      `}</style>

      <div
        className="flex-1 min-h-0"
        style={{
          padding: `${scaledPad}px`,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: `${scaledGap}px`,
          alignContent: 'center',
          justifyContent: 'center',
        }}
      >
        {cards.map((card) => {
          const isFlipped = !!flipped.find((c) => c.key === card.key);
          const isMatched = matched.includes(card.pairId);
          const showFront = isFlipped || isMatched;
          const isJustMatched = justMatched.includes(card.key);
          const isWrong = wrongKeys.includes(card.key);

          return (
            <div
              key={card.key}
              className={`mem-card aspect-square ${showFront ? 'mem-is-flipped' : ''} ${isJustMatched ? 'mem-matched' : ''} ${isWrong ? 'mem-wrong' : ''}`}
            >
              <button
                type="button"
                onClick={() => handleFlip(card)}
                disabled={!interactive || isMatched || flipped.length === 2}
                className="w-full h-full block"
                style={{ cursor: interactive && !isMatched ? 'pointer' : 'default' }}
              >
                <div className="mem-inner">
                  {/* Back / sleeve */}
                  <div
                    className="mem-face"
                    style={{
                      background: `linear-gradient(135deg, ${cardBackColor} 0%, ${cardBackColor} 60%, rgba(0,0,0,0.08) 100%)`,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    }}
                  />
                  {/* Front */}
                  <div
                    className="mem-face mem-back"
                    style={{
                      backgroundColor: cardFrontColor,
                      border: `2px solid ${isMatched ? matchedBorderColor : '#e5e7eb'}`,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      padding: `${Math.max(2, Math.round(6 * s))}px`,
                    }}
                  >
                    {card.kind === 'image' ? (
                      <img
                        src={getMediaUrl(card.value)}
                        alt=""
                        className="w-full h-full object-contain"
                        draggable={false}
                      />
                    ) : (
                      <span
                        className="text-center font-semibold leading-tight break-words"
                        style={{
                          fontSize: `${scaledFontSize}px`,
                          color: textColor,
                          fontFamily,
                        }}
                      >
                        {card.value}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Reset button — only in play mode */}
      {!isEditor && cards.length > 0 && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2 pointer-events-none">
          {allMatched && (
            <div className="pointer-events-auto px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-sm font-medium shadow-sm">
              {t('games.memory.finished', { defaultValue: 'Готово!' })}
            </div>
          )}
          <button
            onClick={resetBoard}
            className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            title={t('games.memory.reset', { defaultValue: 'Почати знову' })}
          >
            <RotateCcw size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MemoryElement;
